import React from 'react';
import { PopoverBody, PopoverHeader, PopperContent } from 'reactstrap';
import { tabbableElements, tabbablesAfter } from '~/lib/helper/FocusHelper';
import { idGenerator } from '~/lib/helper/IdHelper';
import type { Placement } from '~/lib/helper/ReactstrapTypes';
import './HelpPopover.scss';

// TypeScript declaration for build-time process.env substitution
declare const process: {
    env: {
        NODE_ENV: string;
    };
};

const triggerIdGen = idGenerator('help-popover-trigger-');
const contentIdGen = idGenerator('help-popover-content-');
const headerIdGen = idGenerator('help-popover-header-');
const bodyIdGen = idGenerator('help-popover-body-');

interface HelpPopoverProps {
    /** Accessible name for the icon-only trigger button (the icon itself is decorative); also names a headerless popover. */
    label: string;
    /** Popover content, which may include controls. */
    children: React.ReactNode;
    /** Heading rendered above the content; it also names the popover. */
    header?: React.ReactNode;
    /** Trigger icon class (without the leading `icon`). Defaults to a help circle. */
    icon?: string;
    placement?: Placement;
    /**
     * Placements to try, in order, when `placement` does not fit the viewport. Popper's own fallback is only the
     * opposite side, which is no better beside a trigger with too little room on either side of it.
     */
    fallbackPlacements?: Placement[];
    /** Popper `[skidding, distance]` from the trigger, in px. Defaults to `[0, 8]`, the spacing reactstrap's `Popover` uses. */
    offset?: [number, number];
    /** Extra class(es) on the trigger button. */
    className?: string;
    /** Class applied to the popover popper element. */
    popperClassName?: string;
}

interface HelpPopoverState {
    isOpen: boolean;
}

/**
 * Icon-triggered popover, shown as a non-modal dialog that takes focus and sits in the tab order right after its
 * trigger, so it works with the trigger inside a heading or table header and whether or not its content holds controls.
 *
 * The trigger carries `aria-expanded` and `aria-controls` but not `aria-haspopup="dialog"`, whose screen-reader
 * support is uneven; `aria-expanded` already says the button reveals content. The dialog is named by `header`, or by
 * `label` without one, and described by its body, so a screen reader reads the body even when focus lands on a control.
 *
 * The dialog is portalled to the end of `<body>`, so focus is steered as if it followed the trigger:
 * - Open: focus moves to the first control inside, or to the dialog when it holds none.
 * - Tab past the last control, or from a control-less dialog: closes, and focus moves to the first element after
 *   the trigger that accepts it, or back to the trigger when none does; with nothing after the trigger, Tab leaves
 *   the page as usual.
 * - Shift+Tab from the first control or the dialog: focus returns to the trigger, and the dialog stays open.
 * - Escape: closes, and focus returns to the trigger.
 * - A click outside, or focus moving elsewhere: closes.
 *
 * Built on reactstrap's `PopperContent` rather than its `Popover`, which hard-codes `role="tooltip"` (a tooltip
 * opens on hover or focus and must not hold controls) and manages no focus.
 *
 * Not supported inside a reactstrap `Modal`: the portalled dialog falls outside the modal's `aria-modal` scope, and
 * its key events bubble to the modal, whose Escape handler would close the modal too and whose Tab handler would wrap
 * focus. Supporting one means rendering the dialog inside the modal through `PopperContent`'s `container` and
 * stopping Escape's keyup as well as its keydown.
 */
export default class HelpPopover extends React.Component<HelpPopoverProps, HelpPopoverState> {
    private readonly triggerId = triggerIdGen();
    private readonly contentId = contentIdGen();
    private readonly headerId = headerIdGen();
    private readonly bodyId = bodyIdGen();
    private readonly triggerRef = React.createRef<HTMLButtonElement>();
    private readonly dialogRef = React.createRef<HTMLDivElement>();

    state: HelpPopoverState = { isOpen: false };

    // Logged, not thrown, so the misuse fails the consumer's tests without unmounting its tree.
    componentDidMount() {
        if (process.env.NODE_ENV !== 'production' && this.triggerRef.current?.closest('.modal')) {
            console.error(
                'HelpPopover: rendered inside a modal (CancellableModal, ConfirmModal or reactstrap Modal), which it does not support. See PanelSite.md "Popover disclosures".'
            );
        }
    }

    componentDidUpdate(_prevProps: HelpPopoverProps, prevState: HelpPopoverState) {
        if (this.state.isOpen && !prevState.isOpen) {
            // Capture phase, so a container that stops click propagation cannot keep the popover open.
            document.addEventListener('click', this.onDocumentClick, true);
            this.focusDialog();
        } else if (!this.state.isOpen && prevState.isOpen) {
            document.removeEventListener('click', this.onDocumentClick, true);
        }
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.onDocumentClick, true);
    }

    private contains(node: EventTarget | null): boolean {
        return (
            node instanceof Node &&
            (!!this.triggerRef.current?.contains(node) || !!this.dialogRef.current?.contains(node))
        );
    }

    private close() {
        this.setState({ isOpen: false });
    }

    // The dialog itself takes focus when it holds no control; either way the body is its description, so a screen
    // reader reads the help even when focus lands on a control after it.
    // preventScroll, as Popper has not positioned the dialog yet.
    private focusDialog() {
        const dialog = this.dialogRef.current;
        (dialog && (tabbableElements(dialog)[0] ?? dialog))?.focus({ preventScroll: true });
    }

    toggle = () => {
        this.setState((prev) => ({ isOpen: !prev.isOpen }));
    };

    onDocumentClick = (e: MouseEvent) => {
        if (!this.contains(e.target)) this.close();
    };

    onTriggerKeyDown = (e: React.KeyboardEvent) => {
        if (!this.state.isOpen) return;
        if (e.key === 'Escape') {
            e.stopPropagation();
            this.close();
        } else if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            this.focusDialog();
        }
    };

    // The dialog is portalled to the end of the page, so Tab is steered as if it followed the trigger.
    onDialogKeyDown = (e: React.KeyboardEvent) => {
        const dialog = this.dialogRef.current;
        const trigger = this.triggerRef.current;
        if (!dialog || !trigger) return;

        if (e.key === 'Escape') {
            e.stopPropagation();
            this.close();
            trigger.focus();
            return;
        }
        if (e.key !== 'Tab') return;

        const stops = tabbableElements(dialog);
        if (e.shiftKey && (e.target === dialog || e.target === stops[0])) {
            e.preventDefault();
            trigger.focus();
        } else if (!e.shiftKey && e.target === (stops[stops.length - 1] ?? dialog)) {
            this.close();
            // With nothing after the trigger, Tab carries on out of the page as it would from the trigger.
            const following = tabbablesAfter(trigger, dialog);
            if (following.length > 0) {
                e.preventDefault();
                // Where visibility cannot be checked, a hidden element refuses focus: try the rest, then the trigger
                // rather than leave focus on <body>.
                const landed = following.find((el) => {
                    el.focus();
                    return document.activeElement === el;
                });
                if (!landed) trigger.focus();
            }
        }
    };

    // Closes once focus moves elsewhere on the page, so an open popover is not left covering what the
    // keyboard user has moved on to. A null relatedTarget is a click on nothing focusable, which the
    // document click handler decides.
    onBlur = (e: React.FocusEvent) => {
        if (e.relatedTarget && !this.contains(e.relatedTarget)) this.close();
    };

    render() {
        const {
            label,
            children,
            header,
            icon = 'ion-md-help-circle-outline',
            placement = 'top',
            fallbackPlacements,
            offset = [0, 8],
            className,
            popperClassName,
        } = this.props;
        const { isOpen } = this.state;
        return (
            <>
                <button
                    type="button"
                    ref={this.triggerRef}
                    id={this.triggerId}
                    className={['helpPopoverToggle', className].filter(Boolean).join(' ')}
                    aria-label={label}
                    aria-expanded={isOpen}
                    aria-controls={this.contentId}
                    onClick={this.toggle}
                    onKeyDown={this.onTriggerKeyDown}
                    onBlur={this.onBlur}
                >
                    <i className={`icon ${icon}`} aria-hidden />
                </button>
                {/* Mounted only while open, as PopperContent resolves its target on every render. */}
                {isOpen && (
                    <PopperContent
                        target={this.triggerId}
                        isOpen={isOpen}
                        placement={placement}
                        placementPrefix="bs-popover"
                        fallbackPlacements={fallbackPlacements}
                        offset={offset}
                        popperClassName={['popover', 'show', popperClassName].filter(Boolean).join(' ')}
                        arrowClassName="popover-arrow"
                        fade={false}
                    >
                        <div
                            ref={this.dialogRef}
                            id={this.contentId}
                            role="dialog"
                            aria-labelledby={header != null ? this.headerId : this.triggerId}
                            aria-describedby={this.bodyId}
                            tabIndex={-1}
                            className="popover-inner"
                            onKeyDown={this.onDialogKeyDown}
                            onBlur={this.onBlur}
                        >
                            {header != null && <PopoverHeader id={this.headerId}>{header}</PopoverHeader>}
                            <PopoverBody id={this.bodyId}>{children}</PopoverBody>
                        </div>
                    </PopperContent>
                )}
            </>
        );
    }
}
